import {useEffect, useState} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import HeaderNav from '@jobseeker/feature-navigation/components/Header/Containers/WithNav';
import {userProfile} from '@jobseeker/common-core/helpers';
import {
  logout,
  getNotificationCount,
  getNotificationList,
  markNotificationRead,
} from '@jobseeker/feature-navigation/helpers';

import type {AppState} from '@jobseeker/common-core/types';
import {getSavedJobCount} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import type {HeaderPropType} from './types';
import {
  handleTracking,
  TrackEventUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {
  isInAISearchExperiment,
  updateCountInTitle,
} from '@jobseeker/feature-navigation/utils';
import {NOTIFICATION_STATUS} from '@jobseeker/common-core/constants';
import {getAppliedJobCount} from '@jobseeker/common-core/reducers/job/thunkServices/jobdetail';

const HeaderWithNav = (props: HeaderPropType) => {
  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';
  const {
    config: {
      showLogo = true,
      showCourses = true,
      showLoginMenu = true,
      showNavMenu = true,
    },
    ...prop
  } = props || {};

  const {
    profile: {
      id,
      name,
      email,
      picUrl,
      proMembership,
      followUpsRemaining,
      subscriptions,
      showSpotLight,
    },
    profileLoading,
    completeness: {percentage, profileStatus},
    savedJobCount,
    appliedJobCount,
    interviewSlots,
  } = useSelector((state: AppState) => state.user);

  const {userId} = useSelector((state: AppState) => state.auth);
  const [aiSearchActive, setAiSearchActive] = useState<boolean>(false);
  const {profile} = useSelector((state: AppState) => state.user, shallowEqual);

  useEffect(() => {
    const stored = localStorage.getItem('aiSearchActive');
    setAiSearchActive(stored === 'true');

    const handleCustom = () => {
      const updated = localStorage.getItem('aiSearchActive');
      setAiSearchActive(updated === 'true');
    };
    window.addEventListener('aiSearchChange', handleCustom);
    return () => {
      window.removeEventListener('aiSearchChange', handleCustom);
    };
  }, []);

  const {
    layout: {validated, enableChatSticky},
    context: {reCap},
  } = useSelector((state: AppState) => state.app);

  const {selected} = useSelector(
    (state: AppState) => state.app.context.navigation
  );

  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);

  const {
    notification,
    context: {chatCount},
  } = useSelector((state: AppState) => state.app);

  const dispatch: any = useDispatch();

  useEffect(() => {
    if (!savedJobCount && userId !== '-1' && isLoggedIn) {
      dispatch(getSavedJobCount());
    }
  }, [userId, isLoggedIn]);

  const handleNotificationRead = (
    notificationId: number,
    notificationStatus: number,
    type: number
  ) => {
    if (notificationStatus !== NOTIFICATION_STATUS.READ) {
      markNotificationRead(notificationId, type);
      // fetch the updated notification list
      getNewNotificationCount();
    }
  };

  const getNewNotificationCount = async () => {
    try {
      await getNotificationCount(dispatch);
      await getNotificationList(dispatch);
    } catch (error) {
      if (typeof error === 'object' && error) {
        console.log(
          '🚀 ~ file: index.tsx:114 ~ getNewNotificationCount ~ error:',
          error
        );
      }
    }
  };

  useEffect(() => {
    const getProfile = async (): Promise<void> => {
      const profileCall = ['jobdetail', 'jobapplied', 'recruiterview'].includes(
        props?.page
      )
        ? 'profile?apply=true&cohort=true'
        : 'profile?cohort=true';

      await userProfile(profileCall, dispatch);
    };

    if (userId !== '-1' && isLoggedIn) {
      getProfile();
    }
  }, [userId, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      getNewNotificationCount();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (notification?.count > 0) {
      document.title = updateCountInTitle(document.title, notification.count);
    }
  }, [notification.count]);

  useEffect(() => {
    if (!appliedJobCount && !isAppIimjobs && userId !== '-1' && isLoggedIn) {
      dispatch(getAppliedJobCount({}));
    }
  }, [userId, isLoggedIn]);

  useEffect(() => {
    const numericUserId: number = profile?.id;
    const aiSearchStored = localStorage.getItem('aiSearchActive');

    if (aiSearchStored === null) {
      if (isInAISearchExperiment(numericUserId)) {
        setAiSearchActive(true);
        localStorage.setItem('aiSearchActive', 'true');
      } else {
        setAiSearchActive(false);
        localStorage.setItem('aiSearchActive', 'false');
      }
    }
  }, [profile]);

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const categoryId = useSelector(
    (state: AppState) => state.app.context.navigation.actualCategory,
    shallowEqual
  );
  const tagId = useSelector(
    (state: AppState) => state.app.context.navigation.tag,
    shallowEqual
  );

  const handleSearchBar = () => {
    TrackEventUBA('searchIconClick', aiSearchActive ? 'click_AI' : 'click', {
      page: getUbaPageName(),
      categoryID: page == 'category' ? categoryId : undefined,
      tagID: page == 'tag' ? Number(tagId) : undefined,
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
    dispatch({
      type: 'AppContext/set_open_searchoverlay',
      payload: {search: true},
    });
  };

  const handleChatEnabled = () => {
    dispatch({
      type: 'AppContext/set_enable_chat_sticky',
      payload: !enableChatSticky,
    });
  };

  const handleLogout = async () => {
    handleTracking('jsClickLogin_LoggedOut', {
      event_category: 'Reset Password',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
    });
    // remove the userId from the session storage
    sessionStorage.removeItem('userId');
    await logout(['jobfeed', 'recruiterview'].includes(props?.page));
  };

  const profileInfo = {
    id,
    name,
    email,
    picUrl: picUrl || '',
    percentage,
    proMembership: proMembership,
    phoneVerified: profileStatus?.verificationPhone,
    followUpsRemaining,
    showSpotLight,
    subscriptions,
    appliedJobCount,
    interviewSlots: interviewSlots ? interviewSlots.count : 0,
  };

  return (
    <HeaderNav
      config={{
        showLogo: showLogo,
        showLoginMenu: showLoginMenu,
        showNavMenu: showNavMenu,
        showCourses: showCourses,
      }}
      aiSearchActive={aiSearchActive}
      tabsList={props?.meta?.tabsList || []}
      courses={props?.meta?.courses}
      profileDrawerOptions={props?.meta?.drawer || []}
      others={props?.meta?.others || []}
      OnSearchBtnClick={handleSearchBar}
      OnLogoutBtnClick={handleLogout}
      OnChatEnabledBtnClick={handleChatEnabled}
      profileInfo={profileInfo}
      notification={notification}
      courseLink={props?.meta?.course}
      showPremium={
        profileLoading !== null && !profileLoading
          ? !(
              subscriptions?.earlyApplicant.isValid ||
              subscriptions?.resumeBuilder.isValid
            ) &&
            isLoggedIn &&
            !isAppIimjobs
          : false
      }
      chatCount={chatCount}
      selectedNav={selected}
      layoutValidated={validated}
      reCap={reCap}
      readNotification={handleNotificationRead}
      savedJobCount={savedJobCount}
      handleMenuLogin={() => {
        dispatch({
          type: 'AppContext/set_login_popup',
          payload: true,
        });
      }}
      {...prop}
    />
  );
};

export default HeaderWithNav;
